import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { EmptyState } from '../../components/shared/EmptyState';
import { DataTable } from '../../components/shared/DataTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/Dialog';
import { useDocumentTemplates } from '../../core/hooks/useDocumentTemplates';
import { useAuth } from '../../core/contexts/AuthContext';
import type { DocumentTemplateType } from '../../core/models/Document';
import { ColumnDef } from '@tanstack/react-table';

export const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const { templates, loading, error, deleteTemplate } = useDocumentTemplates(organizationId);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<DocumentTemplateType | null>(null);

  const handleDelete = async () => {
    if (templateToDelete) {
      try {
        await deleteTemplate(templateToDelete.id);
        setDeleteDialogOpen(false);
        setTemplateToDelete(null);
      } catch (err) {
        console.error('Error deleting template:', err);
      }
    }
  };

  const columns: ColumnDef<DocumentTemplateType>[] = [
    {
      accessorKey: 'name',
      header: 'Template Name',
      cell: ({ row }) => (
        <div className="font-medium">{row.original.name}</div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600 max-w-md truncate">
          {row.original.description || 'No description'}
        </div>
      ),
    },
    {
      accessorKey: 'dynamicFields',
      header: 'Fields',
      cell: ({ row }) => (
        <Badge variant="default">{row.original.dynamicFields.length} fields</Badge>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'success' : 'default'}>
          {row.original.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) =>
        new Date(row.original.createdAt).toLocaleDateString(),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/documents/templates/${row.original.id}/edit`)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setTemplateToDelete(row.original);
              setDeleteDialogOpen(true);
            }}
          >
            <Trash2 className="h-4 w-4 text-error" />
          </Button>
        </div>
      ),
    },
  ];

  if (loading) {
    return <LoadingState message="Loading templates..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load templates" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Document Templates"
        description="Create and manage document templates"
        icon={FileText}
        actions={
          <Button onClick={() => navigate('/documents/templates/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Create Template
          </Button>
        }
      />

      {templates.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No templates found"
          description="Create your first template to get started"
          action={{
            label: 'Create Template',
            onClick: () => navigate('/documents/templates/new'),
          }}
        />
      ) : (
        <DataTable
          columns={columns}
          data={templates}
          searchKey="name"
          searchPlaceholder="Search templates..."
        />
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
